import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';
import { ButtonType, CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { Mapper, MapperBuilder, TRANSFORM } from 'src/app/helpers/mapper';
import { Router } from '@angular/router';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { AppDataService } from 'src/app/services/app-data.service';
import { TEMPLATES } from 'src/app/constants/templates.constants';
import { API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, FORM_ACTIONS, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES } from 'src/app/constants/app-data.constants';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { ConcessionsApplicationGridReadyForApprovalFilterRequest } from 'src/app/interfaces/concessions-application-grid-ready-for-approval-filter-request.interface';
import { finalize, take } from 'rxjs';
import { ErrorService } from 'src/app/services/error.service';

@Component({
  selector: 'app-concessions-application-ready-for-approval-list',
  templateUrl: './concessions-application-ready-for-approval-list.component.html',
  styleUrl: './concessions-application-ready-for-approval-list.component.scss'
})
export class ConcessionsApplicationReadyForApprovalListComponent extends BaseCardSetListComponent implements OnInit {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }
  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.concessionsApplicationReadyForApprovalList,
      () => API_ENDPOINTS.dashboard.getConcessionsApplicationsReadyForApproval,
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationReferenceNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationTypeFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationAssignedToFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSubmittedOnFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSubmittedOnToFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationYearFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.residentialAddressFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSourceFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationFlaggedForReviewFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationBulkUploadIdFilter)
        .BuildMapper();
  }

  ngOnInit(): void {
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad();
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.concessionsApplicationListForm.applicationListFilter:
      case QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm:
        {
          const applicationFilterRequest = this.getApplicationFilterRequest();
          this.loadGridData(applicationFilterRequest);
          break;
        }
    }
  }

  actionButton_applicationPlaceholder_datagrid_colLink_referenceNumber_Clicked($event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, $event.event.data.id]));
    window.open(url, '_blank');
  }

  actionButton_applicationPlaceholder_datagrid_view_Clicked($event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, $event.event.data.id]);
  }

  actionButton_applicationPlaceholder_datagrid_edit_Clicked($event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, $event.event.data.id]);
  }
  actionButton_applicationPlaceholder_datagrid_delete_Clicked(event: any): void {
    const eventData = event.event.data;
    this.deleteApplication(eventData.id);
  }

  actionButton_applicationPlaceholder_datagrid_custom_bulkApprove_Clicked(event: any): void {
    const eventData = event.event.data;
    this.bulkApprove(eventData);
  }

  actionButton_applicationPlaceholder_datagrid_viewdetails_Clicked(event: any): void {
    this.navigateToConcessionsApplication(event);
  }

  private deleteApplication(id) {
    this.dialogService
      .showConfirm(POP_UP_CODES.concessionsApplicationList.deleteApplicationConfirmation)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            this.appDataService
              .postAndFetchData(`${API_ENDPOINTS.concessionsApplication.delete}/${id}`)
              .pipe(take(1), this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner))
              .subscribe(response => {
                this.onDeleteApplicationSuccess(response);
              });
            break;
          }
          case DialogResult.REJECTED: {
            break;
          }
        }
      });
  }

  onDeleteApplicationSuccess(response: any) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.loadGridData(this.loadGridDataOnFormLoad())
    }
  }

  private bulkApprove(eventData) {
    const selectedIds = eventData.selectedItems;
    if (selectedIds.length <= 0) {
      this.showApplicationSelectionAlert();
    }
    else {
      const ids = selectedIds.map(r => r.id);
      this.appDataService.post(API_ENDPOINTS.concessionsApplication.bulkApprove, { ids: ids })
        .pipe(take(1), this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner))
        .subscribe((response) => {
          this.onBulkApproveSuccess(response);
        });
    }
  }

  showApplicationSelectionAlert() {
    this.dialogService
      .showSimplePopup("Please select at least one application.", "Application Alert!", "Ok")
      .subscribe(
        result => {
        }
      );
  }

  onBulkApproveSuccess(response: any) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      const filterRequest = this.getApplicationFilterRequest();
      this.loadGridDataOnFormLoad(filterRequest);
    }
  }

  private getApplicationFilterRequest(): ConcessionsApplicationGridReadyForApprovalFilterRequest {
    let filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.concessionsApplicationListForm.applications,
      PAGE_NUMBERS.concessionsApplicationListForm.applications,
      SECTION_CODES.concessionsApplicationListForm.application,
      QUESTION_CODES.concessionsApplicationListForm.applicationListFilter
    );
    let mappedFilters = this.filterQuestionToFilterModelMapper.Map(filterModel);

    const applicationQuickSearchTermValue = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.concessionsApplicationListForm.applications,
      PAGE_NUMBERS.concessionsApplicationListForm.applications,
      SECTION_CODES.concessionsApplicationListForm.application,
      QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm
    );

    mappedFilters.applicationQuickSearchTerm = applicationQuickSearchTermValue;
    return mappedFilters;
  }

  navigateToConcessionsApplication(event: any): void {
    const applicationId = event.event.data.id;
    switch (event.event.data.applicationTypeId) {
      case REFERENCE_DATA_IDS.applicationType.nonMainsWater:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsWater.prefix, applicationId]);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsEnergy:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsEnergy.prefix, applicationId]);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsMedicalCooling:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsMedicalCooling.prefix, applicationId]);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsLifeSupport:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsLifeSupport.prefix, applicationId]);
        break;
      case REFERENCE_DATA_IDS.applicationType.edapowGoldCardMunicipalRates:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationEdaAndPowGoldCardMunicipalRates.prefix, applicationId]);
        break;
    }
  }
}
